import { useProfile } from '@ninetailed/experience.js-next';
import laggy from 'api/middleware/laggy';
import { fetchProductDetails } from 'api/product';
import {
  CouponDefinition,
  PersonalDiscountedVariant,
  ProductPriceObj,
} from 'api/product/getPersonalizedProductDetails/types';
import { OrderType, SubscriptionInterval } from 'constants/order';
import { UrlQuery } from 'constants/test/dynamic-ux-test/url-query';
import { useCart } from 'contexts/cart';
import { useNinetailedAbTestContext } from 'contexts/ninetailed-ab-test';
import { usePetContext } from 'contexts/pet';
import { usePetProfile } from 'contexts/pet-profile';
import { useSegmentationContext } from 'contexts/segmentation';
import { useSpecialAbTestContext } from 'contexts/special-ab-test-provider';
import useExperiment from 'hooks/common/use-experiment';
import usePersonalizedProductDetails, {
  isPartialOutput,
} from 'hooks/product/use-personalized-product-details';
import { CrossSellProductDetails } from 'interfaces/product';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSWR from 'swr';
import { showErrorToast } from 'utils/toasts';
import {
  IncludedProduct,
  ProductDetailsPage,
  StaticPDPProduct,
  StaticPdp,
  Variant,
} from '../../types';

interface PropTypes {
  productDetails: ProductDetailsPage['productDetails'];
  initialVariantId?: ProductDetailsPage['initialVariantId'];
  interval?: ProductDetailsPage['interval'];
  defaultPriceVariants?: PersonalDiscountedVariant[];
}

export interface ContextTypes {
  couponPrices: ProductPriceObj | undefined;
  crossSellProductDetails: CrossSellProductDetails | null;
  crossSellProductQuantity: number;
  includedProducts?: IncludedProduct[];
  isProductSegmentLoading: boolean;
  oneOffPrice: number;
  orderType: OrderType;
  price: number;
  pricePerKilo: number;
  product: ProductDetailsPage['productDetails']['product'];
  productSegmentation?: ProductDetailsPage['productSegmentation'];
  /** Layer to be shown on product image based on segmentation */
  segmentationLayerUrl?: string;
  selectedVariant?: Variant;
  selectedVariantId: number | null;
  subscriptionInterval: SubscriptionInterval;
  variantQuantity: number;
  onOrderTypeChange: (orderType: OrderType) => void;
  onSubscriptionIntervalChange: (interval: SubscriptionInterval) => void;
  onVariantIdChange: (variantId: number | null) => void;
  onVariantQuantityChange: (quantity: number) => void;
  setCrossSellProductDetails: React.Dispatch<
    React.SetStateAction<CrossSellProductDetails | null>
  >;
  setCrossSellProductQuantity: React.Dispatch<React.SetStateAction<number>>;
}

export const ProductDetailsHeaderContext = React.createContext<ContextTypes>(
  {} as any
);

/**
 * @TODO : We should streamline How default variant ID is selected since it's unclear.
 */
export const ProductDetailsHeaderProvider: React.FC<
  React.PropsWithChildren<PropTypes>
> = ({
  children,
  productDetails,
  initialVariantId,
  interval = 'one-off',
  defaultPriceVariants,
}) => {
  const router = useRouter();

  // PT-5429: FE: Display discounted price on PDP when coupon code is applied in cart.
  // @see: https://petsdeli.atlassian.net/browse/PT-5429
  useExperiment({
    baseEntryId: '2YR6EsyBVFAKqh7OEANfwE',
    shouldAddToHeader: true,
  });

  const [crossSellProductDetails, setCrossSellProductDetails] =
    useState<CrossSellProductDetails | null>(null);
  const [crossSellProductQuantity, setCrossSellProductQuantity] = useState(0);
  const { testData } = useSpecialAbTestContext();

  const hideVariantsFn = useCallback(
    (product: StaticPDPProduct, variantsToHide: number[]) => {
      product.variants = product.variants.filter(
        (variant) => !variantsToHide.includes(variant.id)
      );
    },
    []
  );

  const { data } = useSWR<StaticPdp | null>(
    () => {
      return productDetails.product.handle;
    },
    fetchProductDetails,
    {
      fallback: { [productDetails.product.handle]: productDetails },
      use: [laggy],
      // Revalidation is done in useOptimizeInterceptor time-aligned with audience header strings
      revalidateOnMount: false,
    }
  );

  if (testData?.hideVariants && data)
    hideVariantsFn(data.product, testData.hideVariants);

  // at this point most of the cases, data is not undefined as we specify fallback data from static props
  const { product, includedProducts: _includedProducts } = data as StaticPdp;

  const isSubscriptionInterval =
    interval &&
    interval !== 'one-off' &&
    Object.values(SubscriptionInterval).includes(interval);

  const [orderType, setOrderType] = useState<OrderType>(
    isSubscriptionInterval ? OrderType.SUBSCRIPTION : OrderType.ONE_OFF
  );

  const [variantQuantity, setVariantQuantity] = useState(1);

  const isInitialVariantIdAvailable = useMemo(() => {
    return productDetails.product.variants.some(
      (variant) => variant.id === initialVariantId
    );
  }, [productDetails.product.variants, initialVariantId]);

  /** Set up defaultVariantId */
  const defaultVariantId = useMemo(() => {
    if (testData?.hideVariants)
      hideVariantsFn(productDetails.product, testData.hideVariants);

    const firstAvailableCheapestVariant = productDetails.product.variants
      .filter((variant) => variant.availableForSale)
      .sort((a, b) => +a.priceV2.amount - +b.priceV2.amount)[0];

    if (initialVariantId && isInitialVariantIdAvailable) {
      return initialVariantId;
    } else if (firstAvailableCheapestVariant) {
      return firstAvailableCheapestVariant?.id;
    } else return productDetails.product.variants[0]?.id;
  }, [
    testData?.hideVariants,
    hideVariantsFn,
    productDetails.product,
    initialVariantId,
    isInitialVariantIdAvailable,
  ]);

  useEffect(() => {
    if (!isInitialVariantIdAvailable && initialVariantId) {
      router.push(`/products/${product.handle}?variant=${defaultVariantId}`);
      showErrorToast({
        error: 'error:products:product-not-available',
        caller: 'ProductDetailsHeaderProvider',
      });
    }
  }, [
    defaultVariantId,
    initialVariantId,
    isInitialVariantIdAvailable,
    product,
    router,
  ]);

  const [selectedVariantId, setSelectedVariantId] =
    useState<number>(defaultVariantId);

  useEffect(() => {
    setSelectedVariantId(defaultVariantId);
  }, [product, defaultVariantId]);

  const [subscriptionInterval, setSubscriptionInterval] =
    useState<SubscriptionInterval>(
      isSubscriptionInterval
        ? (interval as SubscriptionInterval)
        : SubscriptionInterval.FOUR_WEEKS
    );

  const selectedVariant = useMemo(() => {
    return (
      product && product.variants.find(({ id }) => id === selectedVariantId)
    );
  }, [selectedVariantId, product]);

  const isInit = useRef(true);

  useEffect(() => {
    if (!isInit.current) {
      setSelectedVariantId(defaultVariantId);
    }
    isInit.current = false;
  }, [defaultVariantId]);

  const debouncedRouterReplace = useRef(
    debounce((newVariantId, router, product, setSelectedVariantId) => {
      router
        .replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              handle: product.handle,
              variant: newVariantId,
            },
          },
          undefined,
          { shallow: true, scroll: false }
        )
        .then(() => setSelectedVariantId(newVariantId));
    }, 300)
  );

  const onVariantIdChange = useCallback(
    (newVariantId: number | null) => {
      debouncedRouterReplace.current(
        newVariantId,
        router,
        product,
        setSelectedVariantId
      );
    },
    [router, product, setSelectedVariantId]
  );

  const onVariantQuantityChange = useCallback(
    (newQuantity: number) => {
      if (!selectedVariantId) return;
      setVariantQuantity(newQuantity);
    },
    [selectedVariantId]
  );

  const { multipleSegmentation } = useSegmentationContext();
  const { couponCode } = useCart();
  const { experimentHeaderStr } = useNinetailedAbTestContext();
  const { profile } = useProfile();
  const { current: currentPetType } = usePetContext();

  const { query, isReady } = useRouter();
  const multiSegmentMode = query[UrlQuery.multiSegmentMode] as
    | string
    | undefined;

  const { petProfile } = usePetProfile();

  const { personalizedProductDetails, isLoading } =
    usePersonalizedProductDetails({
      petProfile,
      segmentation: multipleSegmentation,
      productId: productDetails.product.id + '',
      couponCode,
      defaultPriceVariants,
      ninetailed: experimentHeaderStr?.split(','),
      audiences: profile?.audiences,
      isReady,
      multiSegmentMode: !!multiSegmentMode,
      currentPetType,
    });

  const _productSegmentation = useMemo(() => {
    if (!isPartialOutput(personalizedProductDetails)) {
      return personalizedProductDetails;
    }

    return null;
  }, [personalizedProductDetails]);

  const includedProducts = useMemo(() => {
    const index = product.variants.findIndex(
      ({ id }) => id === selectedVariantId
    );

    if (index === -1) {
      return undefined;
    }

    return _includedProducts[index];
  }, [product, _includedProducts, selectedVariantId]);

  const prices = useMemo(() => {
    const variant = personalizedProductDetails?.variants?.find(
      (v) => v.id === selectedVariantId + ''
    );

    const oneOffPrice = Number(
      variant?.one_off.compare?.after || variant?.one_off.price || 0
    );
    const subscriptionPrice = Number(variant?.subscription?.compare.after || 0);

    const productWeight =
      selectedVariant &&
      (selectedVariant.weightUnit === 'GRAMS'
        ? selectedVariant.weight / 1000
        : selectedVariant.weight);
    const pricePerKilo =
      (orderType === OrderType.SUBSCRIPTION ? subscriptionPrice : oneOffPrice) /
      (productWeight || 1);

    return { oneOffPrice, subscriptionPrice, pricePerKilo };
  }, [
    orderType,
    personalizedProductDetails?.variants,
    selectedVariant,
    selectedVariantId,
  ]);

  const couponPrices = useMemo(() => {
    let coupon: CouponDefinition | null = null;
    if (
      personalizedProductDetails &&
      !isPartialOutput(personalizedProductDetails) &&
      personalizedProductDetails.coupon
    ) {
      coupon = personalizedProductDetails.coupon;
    }

    return {
      variants: personalizedProductDetails?.variants || [],
      coupon,
    };
  }, [personalizedProductDetails]);

  return (
    <ProductDetailsHeaderContext.Provider
      value={{
        couponPrices,
        crossSellProductDetails,
        crossSellProductQuantity,
        includedProducts,
        oneOffPrice: prices.oneOffPrice,
        orderType,
        price: prices.subscriptionPrice,
        pricePerKilo: prices.pricePerKilo,
        product,
        productSegmentation: _productSegmentation,
        isProductSegmentLoading: isLoading,
        selectedVariant: selectedVariant,
        selectedVariantId,
        subscriptionInterval,
        variantQuantity,
        onOrderTypeChange: setOrderType,
        onSubscriptionIntervalChange: setSubscriptionInterval,
        onVariantIdChange,
        onVariantQuantityChange,
        setCrossSellProductDetails,
        setCrossSellProductQuantity,
      }}
    >
      {children}
    </ProductDetailsHeaderContext.Provider>
  );
};
export const useProductDetailsHeaderState = (): ContextTypes =>
  useContext(ProductDetailsHeaderContext);
export type PdpContextTypes = ContextTypes;
